import { headerMediaQueryLarge } from "@theme/header-breakpoint";
import { Component } from "@theme/component";
import { removeTrapFocus, trapFocus } from "@theme/focus";
import { onAnimationEnd } from "@theme/utilities";

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ["details"];
  #isViewportSyncBound = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("keyup", this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keyup", this.#onKeyUp);
    this.#removeViewportSync();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== "Escape") return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute("open");
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest("details") ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} [event]
   */
  toggle(event) {
    // Keep <details>/<summary> state fully controlled to avoid
    // native toggling side effects (scroll jumps and state drift).
    event?.preventDefault();

    return this.isOpen ? this.close() : this.open(event);
  }

  /**
   * On mobile, calculate the header's bottom position and set it as
   * the drawer offset so the drawer starts right below the header bar.
   */
  #updateDrawerOffset() {
    if (headerMediaQueryLarge.matches) return;

    const header = this.closest(".header");
    const headerRect =
      header instanceof HTMLElement ? header.getBoundingClientRect() : null;
    const measuredHeaderHeight = headerRect ? headerRect.height : 0;

    const bodyStyles = getComputedStyle(document.body);
    const headerHeight =
      Number.parseFloat(bodyStyles.getPropertyValue("--header-height")) || 0;
    const drawerTopBase =
      measuredHeaderHeight > 0
        ? Math.round(measuredHeaderHeight)
        : Math.round(headerHeight);

    if (!drawerTopBase) return;

    const drawer = this.querySelector(".menu-drawer");
    const backdrop = this.querySelector(".menu-drawer__backdrop");

    if (drawer instanceof HTMLElement) {
      drawer.style.top = `${drawerTopBase}px`;
      drawer.style.height = `calc(100dvh - ${drawerTopBase}px)`;
    }

    if (backdrop instanceof HTMLElement) {
      backdrop.style.top = `${drawerTopBase}px`;
      backdrop.style.height = `calc(100dvh - ${drawerTopBase}px)`;
    }
  }

  #handleViewportSync = () => {
    if (!this.isOpen) return;
    this.#updateDrawerOffset();
  };

  #addViewportSync() {
    if (this.#isViewportSyncBound) return;
    this.#isViewportSyncBound = true;

    window.addEventListener("resize", this.#handleViewportSync, {
      passive: true,
    });
    window.addEventListener("scroll", this.#handleViewportSync, {
      passive: true,
    });
  }

  #removeViewportSync() {
    if (!this.#isViewportSyncBound) return;
    this.#isViewportSyncBound = false;

    window.removeEventListener("resize", this.#handleViewportSync);
    window.removeEventListener("scroll", this.#handleViewportSync);
  }

  /**
   * Toggle header state while the mobile drawer is open.
   * - Transparent headers should render in their solid/open style.
   * - Mobile headers should stay pinned to the viewport top while the drawer is open.
   *
   * @param {boolean} isOpen
   */
  #setTransparentHeaderDrawerState(isOpen) {
    const header = this.closest(".header");
    const headerComponent = this.closest("header-component");

    if (!headerMediaQueryLarge.matches && headerComponent instanceof HTMLElement) {
      if (isOpen) {
        headerComponent.dataset.mobileDrawerOpen = "true";
      } else {
        delete headerComponent.dataset.mobileDrawerOpen;
      }
    }

    if (
      !(header instanceof HTMLElement) ||
      !header.hasAttribute("transparent")
    ) {
      return;
    }

    if (isOpen) {
      header.dataset.mobileDrawerOpen = "true";
      return;
    }

    delete header.dataset.mobileDrawerOpen;
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector("summary");

    if (!summary) return;

    this.#setTransparentHeaderDrawerState(true);
    this.#addViewportSync();
    this.#updateDrawerOffset();

    details.setAttribute("open", "");
    summary.setAttribute("aria-expanded", "true");
    requestAnimationFrame(() => {
      this.#updateDrawerOffset();
      details.classList.add("menu-open");
      requestAnimationFrame(() => {
        this.#updateDrawerOffset();
      });
      setTimeout(() => {
        this.#updateDrawerOffset();
      }, 120);
      setTimeout(() => {
        this.#updateDrawerOffset();
      }, 260);
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector("summary");

    if (!summary) return;

    summary.setAttribute("aria-expanded", "false");
    details.classList.remove("menu-open");

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        this.#removeViewportSync();
        this.#setTransparentHeaderDrawerState(false);
        const openDetails = this.querySelectorAll("details[open]");
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty("will-change", "unset");
        target.removeEventListener(
          "animationend",
          removeWillChangeOnAnimationEnd,
        );
      }
    }
    const allAnimated = this.querySelectorAll(".menu-drawer__animated-element");
    allAnimated.forEach((element) => {
      element.addEventListener("animationend", removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get("header-drawer")) {
  customElements.define("header-drawer", HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove("menu-open");
  element.removeAttribute("open");
  element.querySelector("summary")?.setAttribute("aria-expanded", "false");
}
